import type { SqlDriver } from "./driver";

export type Migration = {
  version: number;
  /** One SQL statement per entry. Drivers may not support multi-statement strings with params. */
  statements: readonly string[];
};

/**
 * Ordered, additive-only schema migrations. The same scripts run on every platform and in
 * tests. Never edit a published version; add a new one. A migration that can lose data is
 * not allowed: add a table or column and leave the old one in place.
 *
 * Every entity table keeps the full entity JSON in `data` so reads round-trip exactly and
 * absent optional fields stay absent. The other columns exist for coarse queries and FTS.
 */
export const MIGRATIONS: readonly Migration[] = [
  {
    version: 1,
    statements: [
      // tasks
      `CREATE TABLE tasks (
        id TEXT PRIMARY KEY,
        title TEXT NOT NULL,
        description TEXT,
        project_id TEXT,
        status TEXT NOT NULL,
        due_date TEXT,
        scheduled_at TEXT,
        parent_task_id TEXT,
        deleted_at TEXT,
        updated_at TEXT NOT NULL,
        data TEXT NOT NULL
      )`,
      `CREATE INDEX tasks_project_id ON tasks(project_id)`,
      `CREATE INDEX tasks_status ON tasks(status)`,
      `CREATE INDEX tasks_due_date ON tasks(due_date)`,
      `CREATE INDEX tasks_scheduled_at ON tasks(scheduled_at)`,
      `CREATE INDEX tasks_parent_task_id ON tasks(parent_task_id)`,
      `CREATE VIRTUAL TABLE tasks_fts USING fts5(title, description, content='tasks', content_rowid='rowid')`,
      `CREATE TRIGGER tasks_ai AFTER INSERT ON tasks BEGIN
        INSERT INTO tasks_fts(rowid, title, description) VALUES (new.rowid, new.title, new.description);
      END`,
      `CREATE TRIGGER tasks_ad AFTER DELETE ON tasks BEGIN
        INSERT INTO tasks_fts(tasks_fts, rowid, title, description) VALUES ('delete', old.rowid, old.title, old.description);
      END`,
      `CREATE TRIGGER tasks_au AFTER UPDATE ON tasks BEGIN
        INSERT INTO tasks_fts(tasks_fts, rowid, title, description) VALUES ('delete', old.rowid, old.title, old.description);
        INSERT INTO tasks_fts(rowid, title, description) VALUES (new.rowid, new.title, new.description);
      END`,

      // notes
      `CREATE TABLE notes (
        id TEXT PRIMARY KEY,
        title TEXT NOT NULL,
        markdown TEXT NOT NULL,
        folder_id TEXT,
        pinned INTEGER NOT NULL DEFAULT 0,
        deleted_at TEXT,
        updated_at TEXT NOT NULL,
        data TEXT NOT NULL
      )`,
      `CREATE INDEX notes_folder_id ON notes(folder_id)`,
      `CREATE INDEX notes_pinned ON notes(pinned)`,
      `CREATE INDEX notes_title ON notes(title)`,
      `CREATE VIRTUAL TABLE notes_fts USING fts5(title, markdown, content='notes', content_rowid='rowid')`,
      `CREATE TRIGGER notes_ai AFTER INSERT ON notes BEGIN
        INSERT INTO notes_fts(rowid, title, markdown) VALUES (new.rowid, new.title, new.markdown);
      END`,
      `CREATE TRIGGER notes_ad AFTER DELETE ON notes BEGIN
        INSERT INTO notes_fts(notes_fts, rowid, title, markdown) VALUES ('delete', old.rowid, old.title, old.markdown);
      END`,
      `CREATE TRIGGER notes_au AFTER UPDATE ON notes BEGIN
        INSERT INTO notes_fts(notes_fts, rowid, title, markdown) VALUES ('delete', old.rowid, old.title, old.markdown);
        INSERT INTO notes_fts(rowid, title, markdown) VALUES (new.rowid, new.title, new.markdown);
      END`,

      // folders
      `CREATE TABLE folders (
        id TEXT PRIMARY KEY,
        parent_id TEXT,
        deleted_at TEXT,
        updated_at TEXT NOT NULL,
        data TEXT NOT NULL
      )`,
      `CREATE INDEX folders_parent_id ON folders(parent_id)`,

      // projects
      `CREATE TABLE projects (
        id TEXT PRIMARY KEY,
        status TEXT NOT NULL,
        deleted_at TEXT,
        updated_at TEXT NOT NULL,
        data TEXT NOT NULL
      )`,
      `CREATE INDEX projects_status ON projects(status)`,

      // reminders. `fire_at` is the next instant the reminder fires (trigger or snooze), NULL when it will not.
      `CREATE TABLE reminders (
        id TEXT PRIMARY KEY,
        status TEXT NOT NULL,
        trigger_at TEXT NOT NULL,
        fire_at TEXT,
        target_id TEXT,
        deleted_at TEXT,
        updated_at TEXT NOT NULL,
        data TEXT NOT NULL
      )`,
      `CREATE INDEX reminders_status ON reminders(status)`,
      `CREATE INDEX reminders_trigger_at ON reminders(trigger_at)`,
      `CREATE INDEX reminders_fire_at ON reminders(fire_at)`,
      `CREATE INDEX reminders_target_id ON reminders(target_id)`,

      // outbound mutation queue
      `CREATE TABLE mutations (
        seq INTEGER PRIMARY KEY AUTOINCREMENT,
        mutation_id TEXT NOT NULL UNIQUE,
        device_id TEXT NOT NULL,
        created_at TEXT NOT NULL,
        target_id TEXT NOT NULL,
        mutation TEXT NOT NULL,
        attempts INTEGER NOT NULL DEFAULT 0,
        last_error TEXT
      )`,
      `CREATE INDEX mutations_target_id ON mutations(target_id)`,

      // sync state, one JSON value per key (deviceId, cursor, lastPushAt, ...)
      `CREATE TABLE sync_state (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL
      )`,

      // per-device settings, JSON values
      `CREATE TABLE kv (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL
      )`,
    ],
  },
  {
    version: 2,
    statements: [
      `CREATE TABLE note_links (
        source_note_id TEXT NOT NULL,
        target TEXT NOT NULL,
        target_kind TEXT NOT NULL CHECK (target_kind IN ('id', 'title')),
        PRIMARY KEY (source_note_id, target, target_kind)
      )`,
      `CREATE INDEX note_links_target ON note_links(target_kind, target)`,
    ],
  },
  {
    version: 3,
    statements: [
      `ALTER TABLE mutations ADD COLUMN attempted INTEGER NOT NULL DEFAULT 0`,
      // Old clients only recorded failed attempts. Freeze all existing entries: a crash
      // may have interrupted a request before its first failure was recorded.
      `UPDATE mutations SET attempted = 1`,
    ],
  },
  {
    version: 4,
    statements: [
      `CREATE TABLE areas (
        id TEXT PRIMARY KEY,
        deleted_at TEXT,
        updated_at TEXT NOT NULL,
        data TEXT NOT NULL
      )`,
    ],
  },
];

export const LATEST_SCHEMA_VERSION = MIGRATIONS[MIGRATIONS.length - 1]?.version ?? 0;

/**
 * Create `schema_migrations` if needed and apply every pending version in order, each in
 * its own transaction. Returns the highest version recorded afterwards.
 */
export async function runMigrations(driver: SqlDriver): Promise<number> {
  await driver.exec(
    `CREATE TABLE IF NOT EXISTS schema_migrations (version INTEGER PRIMARY KEY, applied_at TEXT NOT NULL)`,
  );
  const rows = await driver.query<{ version: number }>(`SELECT version FROM schema_migrations`);
  const applied = new Set(rows.map((r) => r.version));
  const pending = [...MIGRATIONS]
    .filter((m) => !applied.has(m.version))
    .sort((a, b) => a.version - b.version);

  for (const m of pending) {
    try {
      await driver.batch([
        { sql: "BEGIN" },
        ...m.statements.map((sql) => ({ sql })),
        {
          sql: `INSERT INTO schema_migrations (version, applied_at) VALUES (?, ?)`,
          params: [m.version, new Date().toISOString()],
        },
        { sql: "COMMIT" },
      ]);
    } catch (err) {
      await driver.exec("ROLLBACK").catch(() => undefined);
      throw err;
    }
    applied.add(m.version);
  }
  return applied.size === 0 ? 0 : Math.max(...applied);
}
